import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import stockService, { PeseeComplete, MiseEnStockRequest } from '../../services/stockService';

interface StockModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPesees: PeseeComplete[];
  onSuccess: (message: string) => void;
  embedded?: boolean;
}

interface Magasin {
  id: number;
  nom: string;
  code: string;
}

const StockModal: React.FC<StockModalProps> = ({
  isOpen,
  onClose,
  selectedPesees,
  onSuccess,
  embedded = false,
}) => {
  const [magasins, setMagasins] = useState<Magasin[]>([]);
  const [magasinDestination, setMagasinDestination] = useState<number | ''>('');
  const [magasinSource, setMagasinSource] = useState<number | ''>('');
  const [typeSortie, setTypeSortie] = useState<'MAGASIN' | 'DEPOT' | 'USINE' | 'CLIENT' | 'REBUS' | 'AUTRE' | ''>('');
  const [observations, setObservations] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger les magasins
  useEffect(() => {
    const loadMagasins = async () => {
      try {
        const data = await stockService.getMagasins();
        setMagasins(data);
      } catch (error) {
        console.error('Erreur lors du chargement des magasins:', error);
      }
    };

    if (isOpen) {
      loadMagasins();
    }
  }, [isOpen]);

  // Déterminer le type de pesée principal
  const peseeType = selectedPesees.length > 0 ? selectedPesees[0].type_pesee : 'reception';
  const isMixedType = selectedPesees.some(p => p.type_pesee !== peseeType);

  // Gérer la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isMixedType) {
      setError('Toutes les pesées sélectionnées doivent être du même type');
      return;
    }

    if (peseeType === 'reception' && !magasinDestination) {
      setError('Veuillez sélectionner un magasin de destination');
      return;
    }

    if (peseeType === 'expedition' && (!magasinSource || !typeSortie)) {
      setError('Veuillez sélectionner un magasin source et un type de sortie');
      return;
    }

    if (peseeType === 'transfert' && (!magasinSource || !magasinDestination)) {
      setError('Veuillez sélectionner un magasin source et un magasin de destination');
      return;
    }

    setIsLoading(true);

    try {
      // Vérifier que les pesées sélectionnées ne sont pas déjà en stock
      const peseesDejaEnStock = selectedPesees.filter(p => {
        const enStock = (p as any).en_stock;
        return enStock === true || enStock === 'true' || enStock === 1;
      });

      if (peseesDejaEnStock.length > 0) {
        const references = peseesDejaEnStock.map(p => p.reference).join(', ');
        setError(`Les pesées suivantes sont déjà en stock: ${references}. Veuillez actualiser la liste.`);
        setIsLoading(false);
        return;
      }

      const requestData: MiseEnStockRequest = {
        pesees: selectedPesees.map(p => p.id),
        ...(magasinDestination && { magasin_destination: magasinDestination }),
        ...(magasinSource && { magasin_source: magasinSource }),
        ...(typeSortie && { type_sortie: typeSortie }),
        ...(observations && { observations: observations })
      };

      const response = await stockService.mettreEnStock(requestData);
      onSuccess(response.message);
      
      // Réinitialiser le formulaire
      setMagasinDestination('');
      setMagasinSource('');
      setTypeSortie('');
      setObservations('');
    } catch (error: any) {
      console.error('Erreur lors de la mise en stock:', error);
      
      // Extraire le message d'erreur du backend
      let errorMessage = 'Erreur lors de la mise en stock';
      if (error.response?.data) {
        const data = error.response.data;
        if (data.errors?.non_field_errors) {
          errorMessage = Array.isArray(data.errors.non_field_errors) 
            ? data.errors.non_field_errors.join(', ')
            : data.errors.non_field_errors;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.detail) {
          errorMessage = data.detail;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  if (embedded) {
    return (
      <div className="space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Informations sur les pesées sélectionnées */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Pesées sélectionnées ({selectedPesees.length})
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto">
              {selectedPesees.map((pesee) => (
                <div key={pesee.id} className="flex justify-between items-center py-1">
                  <span className="text-sm text-gray-700">
                    {pesee.reference} - {pesee.produit?.nom} ({pesee.poids_net} kg)
                  </span>
                  <span className="text-xs text-gray-500 capitalize">
                    {pesee.type_pesee}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Formulaire */}
          <div className="space-y-4">
            {/* Type de pesée */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de pesée
              </label>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {peseeType}
                </span>
                {isMixedType && (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠️ Toutes les pesées doivent être du même type
                  </p>
                )}
              </div>
            </div>

            {/* Champs selon le type de pesée */}
            {peseeType === 'reception' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Magasin de destination *
                </label>
                <select
                  value={magasinDestination}
                  onChange={(e) => setMagasinDestination(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Sélectionner un magasin</option>
                  {magasins.map(magasin => (
                    <option key={magasin.id} value={magasin.id}>
                      {magasin.nom} ({magasin.code})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {peseeType === 'expedition' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Magasin source *
                  </label>
                  <select
                    value={magasinSource}
                    onChange={(e) => setMagasinSource(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un magasin</option>
                    {magasins.map(magasin => (
                      <option key={magasin.id} value={magasin.id}>
                        {magasin.nom} ({magasin.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de sortie *
                  </label>
                  <select
                    value={typeSortie}
                    onChange={(e) => setTypeSortie(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un type</option>
                    <option value="MAGASIN">Magasin</option>
                    <option value="DEPOT">Dépôt</option>
                    <option value="USINE">Usine</option>
                    <option value="CLIENT">Client</option>
                    <option value="REBUS">Rebus</option>
                    <option value="AUTRE">Autre</option>
                  </select>
                </div>
              </>
            )}

            {peseeType === 'transfert' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Magasin source *
                  </label>
                  <select
                    value={magasinSource}
                    onChange={(e) => setMagasinSource(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un magasin</option>
                    {magasins.map(magasin => (
                      <option key={magasin.id} value={magasin.id}>
                        {magasin.nom} ({magasin.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Magasin de destination *
                  </label>
                  <select
                    value={magasinDestination}
                    onChange={(e) => setMagasinDestination(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un magasin</option>
                    {magasins.map(magasin => (
                      <option key={magasin.id} value={magasin.id}>
                        {magasin.nom} ({magasin.code})
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Observations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observations
              </label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Notes sur la mise en stock..."
              />
            </div>

            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isLoading || isMixedType}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Traitement...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Mettre en stock
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Mise en stock
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Informations sur les pesées sélectionnées */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Pesées sélectionnées ({selectedPesees.length})
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                {selectedPesees.map((pesee) => (
                  <div key={pesee.id} className="flex justify-between items-center py-1">
                    <span className="text-sm text-gray-700">
                      {pesee.reference} - {pesee.produit?.nom} ({pesee.poids_net} kg)
                    </span>
                    <span className="text-xs text-gray-500 capitalize">
                      {pesee.type_pesee}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Formulaire */}
            <div className="space-y-4">
              {/* Type de pesée */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de pesée
                </label>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {peseeType}
                  </span>
                  {isMixedType && (
                    <p className="text-xs text-red-600 mt-1">
                      ⚠️ Toutes les pesées doivent être du même type
                    </p>
                  )}
                </div>
              </div>

              {/* Champs selon le type de pesée */}
              {peseeType === 'reception' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Magasin de destination *
                  </label>
                  <select
                    value={magasinDestination}
                    onChange={(e) => setMagasinDestination(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un magasin</option>
                    {magasins.map(magasin => (
                      <option key={magasin.id} value={magasin.id}>
                        {magasin.nom} ({magasin.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {peseeType === 'expedition' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Magasin source *
                    </label>
                    <select
                      value={magasinSource}
                      onChange={(e) => setMagasinSource(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner un magasin</option>
                      {magasins.map(magasin => (
                        <option key={magasin.id} value={magasin.id}>
                          {magasin.nom} ({magasin.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de sortie *
                    </label>
                    <select
                      value={typeSortie}
                      onChange={(e) => setTypeSortie(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner un type</option>
                      <option value="MAGASIN">Magasin</option>
                      <option value="DEPOT">Dépôt</option>
                      <option value="USINE">Usine</option>
                      <option value="CLIENT">Client</option>
                      <option value="REBUS">Rebus</option>
                      <option value="AUTRE">Autre</option>
                    </select>
                  </div>
                </>
              )}

              {peseeType === 'transfert' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Magasin source *
                    </label>
                    <select
                      value={magasinSource}
                      onChange={(e) => setMagasinSource(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner un magasin</option>
                      {magasins.map(magasin => (
                        <option key={magasin.id} value={magasin.id}>
                          {magasin.nom} ({magasin.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Magasin de destination *
                    </label>
                    <select
                      value={magasinDestination}
                      onChange={(e) => setMagasinDestination(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner un magasin</option>
                      {magasins.map(magasin => (
                        <option key={magasin.id} value={magasin.id}>
                          {magasin.nom} ({magasin.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Observations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observations
                </label>
                <textarea
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Notes sur la mise en stock..."
                />
              </div>

              {/* Message d'erreur */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading || isMixedType}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4 mr-2" />
                      Mettre en stock
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StockModal;